import React, { FC } from 'react';

import { Box, SxProps, Typography } from '@mui/material';

import {
  PharmacistLegendVariant,
  PharmacyLegendVariant,
  TypeOfUser,
  convertToDecimals,
  getTypeOfUser,
  currency,
  perHour,
} from '@pharmaplan/common';

import CustomChip from '../../common/CustomChip';
import { ChipType } from '../../../helpers/Constants';
import { useAppSelector } from '../../../hooks/useAppSelector';
import strings from '../../../localization';

import classes from './style';

interface ServiceCardProps {
  hideDivider?: boolean;
  isPromoBooking?: boolean;
  isCounterOffer?: boolean;
  eventType?: PharmacyLegendVariant | PharmacistLegendVariant | null;
  title?: string | null;
  hourlyRate?: {
    paidHourlyRate: number;
    counterHourlyRate: number;
  };
  customContainerStyle?: SxProps;
  content: React.ReactNode;
  footer?: React.ReactNode;
  onClick?: () => void;
}

const ServiceCard: FC<ServiceCardProps> = ({
  title,
  onClick,
  hideDivider = false,
  isPromoBooking,
  isCounterOffer,
  eventType,
  hourlyRate,
  customContainerStyle,
  content,
  footer,
}) => {
  const userType = useAppSelector(getTypeOfUser);
  const isPharmacy = userType === TypeOfUser.pharmacy;

  const counterOfferCard = () => {
    if (isPharmacy) {
      return {
        dividerStyle: classes.footerDivider,
        background: classes.pharmacyCounterCard,
      };
    }
    return {
      dividerStyle: classes.counterDivider,
      background: classes.pharmacistCounterCard,
    };
  };

  const counterConfig = isCounterOffer
    ? {
      dividerStyle: counterOfferCard().dividerStyle,
      background: counterOfferCard()?.background,
      hourlyRate: hourlyRate?.counterHourlyRate,
    }
    : {
      dividerStyle: classes.footerDivider,
      background: {},
      hourlyRate: hourlyRate?.paidHourlyRate,
    };

  const displayHouryRate = `${currency}${convertToDecimals(
    counterConfig.hourlyRate,
  )}${perHour}`;

  const showHourlyStrike = isCounterOffer
    && hourlyRate?.counterHourlyRate !== hourlyRate?.paidHourlyRate;

  return (
    <Box component="div" onClick={onClick}>
      <Box
        sx={
          [
            classes.root,
            eventType && classes.eventBorder(eventType),
            counterConfig.background,
            onClick && classes.clickable,
            customContainerStyle,
          ] as keyof typeof eventType
        }
      >
        {!!title && (
          <Box sx={classes.titleContainer}>
            <Typography sx={classes.title}>{title}</Typography>
            {hourlyRate && (
              <Box sx={classes.hourlyRateContainer}>
                {showHourlyStrike && (
                  <Typography sx={classes.strike}>
                    $
                    {hourlyRate?.paidHourlyRate}
                    {strings.perHour}
                  </Typography>
                )}
                <CustomChip
                  type={
                    isPromoBooking ? ChipType.promoBooking : ChipType.hourlyRate
                  }
                  label={displayHouryRate}
                />
              </Box>
            )}
          </Box>
        )}

        <Box>{content}</Box>
        {footer && (
          <Box
            sx={[
              hideDivider
                ? classes.footerWithoutDivider
                : counterConfig.dividerStyle,
            ]}
          >
            {footer}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ServiceCard;
